import { useEffect, useRef, useState } from 'react';
import type React from 'react';

import { debounce } from 'lodash-es';

import { Spinner, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { isSsr } from '@change-corgi/core/ssr';
import { getWindow } from '@change-corgi/core/window';
import { memo } from '@change-corgi/core-react/core';
import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import type { GoogleLoginResultData } from '#src/app/shared/hooks/auth';
import { useHandleGoogleLogin } from '#src/app/shared/hooks/auth';
import { isDarkModeApplied, useIsDarkMode } from '#src/app/shared/hooks/dom';
import { useLocale } from '#src/app/shared/hooks/l10n';

import type { GsiButtonConfiguration } from './types';

const baseButtonOptions: Omit<GsiButtonConfiguration, 'theme'> = {
	size: 'large',
	type: 'standard',
	shape: 'rectangular',
	text: 'continue_with',
};

type Props = {
	onSuccess: (data: GoogleLoginResultData) => void;
	onError: (reason: string) => void;
	loginType: string;
	signupContext: string;
	optionalOnClickTrackProps?: TrackingProperties;
};

const RealGoogleAuthButton = memo<Props>(function RealGoogleAuthButton({
	loginType,
	signupContext,
	optionalOnClickTrackProps,
	onSuccess,
	onError,
}): JSX.Element | null {
	const { googleAuth } = useUtilityContext();
	const locale = useLocale();
	const isDarkMode = useIsDarkMode();
	const buttonElement = useRef<HTMLDivElement>(null);
	const [screenWidth, setScreenWidth] = useState<number>(isSsr() ? 0 : getWindow().innerWidth);
	const { handleGoogleLogin, handleGoogleLoginClick, result } = useHandleGoogleLogin({
		signupContext,
		loginType,
		optionalOnClickTrackProps,
	});

	// Dynamically resize the button when window is resized
	useEffect(() => {
		const handleResize = debounce(() => {
			setScreenWidth(getWindow().innerWidth);
		}, 400);
		getWindow().addEventListener('resize', handleResize);

		return () => {
			handleResize.cancel();
			getWindow().removeEventListener('resize', handleResize);
		};
	}, []);

	useEffect(() => {
		if (buttonElement.current) {
			void googleAuth.renderButton({
				handleGoogleSignIn: handleGoogleLogin,
				parent: buttonElement.current,
				buttonOptions: {
					...baseButtonOptions,
					// Google renders this button as its own DOM/iframe, so it doesn't pick up the page's dark: classes —
					// theme has to be switched explicitly to keep it legible against a dark background.
					theme: isDarkModeApplied() ? 'outline_dark' : 'outline',
					width: `${buttonElement.current.clientWidth}px`,
					click_listener: handleGoogleLoginClick,
					locale,
				},
			});
		}
	}, [screenWidth, googleAuth, handleGoogleLogin, handleGoogleLoginClick, locale, isDarkMode]);

	useEffect(() => {
		switch (result.status) {
			case 'success':
				onSuccess(result.data);
				break;
			case 'error':
				onError(result.errorReason);
				break;
			case 'loading':
				break;
			case 'cancelled':
				break;
			default:
				break; // do nothing
		}
	}, [result, onError, onSuccess]);

	if (googleAuth.disabled) return null;

	return (
		<>
			<div ref={buttonElement} data-testid="google-auth-button" />
			{result.status === 'loading' && <FakeGoogleButton loading />}
		</>
	);
});

export const GoogleAuthButton: React.FC<Props> = ({
	loginType,
	signupContext,
	optionalOnClickTrackProps,
	onSuccess,
	onError,
}) => {
	return (
		<ClientRender placeholder={<FakeGoogleButton />}>
			<RealGoogleAuthButton
				loginType={loginType}
				signupContext={signupContext}
				optionalOnClickTrackProps={optionalOnClickTrackProps}
				onSuccess={onSuccess}
				onError={onError}
			/>
		</ClientRender>
	);
};

const FakeGoogleButton: React.FC<{ loading?: boolean }> = ({ loading }) => {
	return (
		<button
			type="button"
			// border/max-width copied from Google's own rendered button to keep the fake and real buttons visually consistent
			className={
				'text-typography-strong border-stroke-weaker relative m-0 h-10 w-full max-w-[400px] cursor-pointer rounded-[5px] border border-solid bg-transparent px-1 py-0'
			}
			data-testid="google-auth-button-fake"
		>
			<div className={cn('flex flex-row items-center gap-2 px-2', loading && 'invisible')}>
				<GoogleIcon aria-hidden="true" className="text-[18px]" />
				<Text as="span" variant="body" size="small" className="flex-1 text-center" data-label>
					<Translate value="corgi.google.continue_with" />
				</Text>
			</div>
			{loading && (
				<div className="absolute top-0 left-0 flex h-full w-full items-center justify-center">
					<Spinner className="size-6" data-testid="google-auth-button-spinner" />
					{/* TODO translate label */}
					<span className="sr-only">loading</span>
				</div>
			)}
		</button>
	);
};

const GoogleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => {
	return (
		<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 48 48" {...props}>
			<g>
				<path
					fill="#EA4335"
					d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
				></path>
				<path
					fill="#4285F4"
					d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
				></path>
				<path
					fill="#FBBC05"
					d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
				></path>
				<path
					fill="#34A853"
					d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
				></path>
				<path fill="none" d="M0 0h48v48H0z"></path>
			</g>
		</svg>
	);
};
